const SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  PLN: "zł",
  GBP: "£",
  JPY: "¥",
  CHF: "CHF",
  CAD: "CA$",
  AUD: "A$",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  CZK: "Kč",
  HUF: "Ft",
  UAH: "₴",
};

export function sanitizeAmountInput(raw: string): string {
  // Normalize comma decimal separator (common on European/Ukrainian keyboards)
  let s = raw.replace(",", ".");
  // Keep only digits and the first decimal point
  let hasDot = false;
  s = s.split("").filter((ch) => {
    if (ch === ".") {
      if (hasDot) return false;
      hasDot = true;
      return true;
    }
    return ch >= "0" && ch <= "9";
  }).join("");
  // Cap to 2 fractional digits while preserving a trailing dot mid-entry
  const dotIndex = s.indexOf(".");
  if (dotIndex !== -1 && s.length - dotIndex - 1 > 2) {
    s = s.slice(0, dotIndex + 3);
  }
  return s;
}

export function currencySymbol(code: string): string {
  return SYMBOLS[code.toUpperCase()] ?? code;
}

// Locale-aware number formatting with thousands grouping (e.g. "1,234.00").
// Grouping is a readability + accessibility requirement for monetary values, and
// the separators follow the user's device locale. Always two fraction digits.
export function formatNumber(amount: string | number): string {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(n)) return "0.00";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatMoney(amount: string | number, currency: string): string {
  return `${currencySymbol(currency)}${formatNumber(amount)}`;
}

// Splits a formatted amount into pieces so the UI can render the integer part
// prominently and de-emphasize the cents (a common banking pattern). Uses
// Intl formatToParts so it stays correct for comma-decimal locales.
export interface MoneyParts {
  sign: string;    // "" or "−" (real minus, never color-alone)
  symbol: string;  // currency symbol/code
  int: string;     // grouped integer digits, e.g. "1,234"
  frac: string;    // decimal separator + cents, e.g. ".00" or ",00"
}

export function splitMoney(amount: string | number, currency: string): MoneyParts {
  const raw = typeof amount === "string" ? parseFloat(amount) : amount;
  const n = isNaN(raw) ? 0 : raw;
  const parts = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(Math.abs(n));
  let int = "";
  let frac = "";
  let afterDecimal = false;
  for (const p of parts) {
    if (p.type === "decimal") { afterDecimal = true; frac += p.value; }
    else if (p.type === "fraction") frac += p.value;
    else if (!afterDecimal && (p.type === "integer" || p.type === "group")) int += p.value;
  }
  return { sign: n < 0 ? "−" : "", symbol: currencySymbol(currency), int, frac };
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelativeTime(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const totalMin = Math.floor(diff / 60_000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}
